////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.6                                                            //
//  Summary: Head/SEO helpers — canonical link + JSON-LD structured data     //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useEffect } from 'react';

import type { CareerListItem, NewsDetailRecord } from '@/types/public';

// Canonical host matches index.html's og:url (www). If the edge ever settles on
// the apex instead, change it here and in index.html together.
export const SITE_ORIGIN = 'https://www.ironside.com.au';

// Mirrors config.tsx app.legalName — one legal name, not a second copy invented here.
const ORG_NAME = 'IRONSIDE Construction Pty Ltd';

function absoluteUrl (path: string): string {
  return /^https?:\/\//i.test(path) ? path : `${SITE_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
}

/** Keeps `<link rel="canonical">` in sync with the current route. Reads
 * `window.location.pathname` inside the effect rather than react-router's
 * useLocation — usePageTitle (the caller) is also mounted by components tested
 * outside a router (test/media.test.tsx), and every real route change mounts a
 * page component (or changes its title), re-running this effect after the URL
 * has already updated. Query strings are deliberately excluded (pagination and
 * filters must not mint duplicate canonical URLs). Admin routes are skipped —
 * robots.txt disallows them. */
export function useCanonical (dep: unknown): void {
  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname.startsWith('/admin')) return;

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const created = link === null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    const previous = link.getAttribute('href');
    link.setAttribute('href', `${SITE_ORIGIN}${pathname}`);

    return () => {
      if (created) link.remove();
      else if (previous !== null) link.setAttribute('href', previous);
    };
  }, [dep]);
}

/** Mounts one `<script type="application/ld+json">` in <head> for the lifetime of
 * the calling component; `null` mounts nothing (loading/error states). Keyed on the
 * serialised JSON so a re-render with an identical object does not churn the DOM. */
export function useJsonLd (json: object | null): void {
  const serialised = json === null ? null : JSON.stringify(json);

  useEffect(() => {
    if (serialised === null) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = serialised;
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [serialised]);
}

const ORGANISATION_REF = {
  '@type': 'Organization',
  name: ORG_NAME,
  url: `${SITE_ORIGIN}/`,
} as const;

/** schema.org NewsArticle for the news detail route. Optional fields are emitted
 * only when the record actually carries them — nothing fabricated. */
export function newsArticleJsonLd (article: NewsDetailRecord): object {
  const json: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    datePublished: article.publishedAt,
    mainEntityOfPage: `${SITE_ORIGIN}/news/${article.slug}`,
    publisher: ORGANISATION_REF,
  };
  if (article.author) json.author = { '@type': 'Person', name: article.author };
  if (article.excerpt) json.description = article.excerpt;
  if (article.heroImage) json.image = [absoluteUrl(article.heroImage)];
  return json;
}

// content.careers employment_type slugs -> Google's recognised employmentType
// values. AU "casual" has no Google equivalent — OTHER, not a lie like PART_TIME.
const EMPLOYMENT_TYPE_MAP: Record<string, string> = {
  full_time: 'FULL_TIME',
  part_time: 'PART_TIME',
  contract: 'CONTRACTOR',
  casual: 'OTHER',
};

/** Superset of CareerListItem — the /public/careers list payload carries no
 * closes_at/salary, but the builder accepts them so a future career-detail route
 * (GET /public/careers/:slug already exists API-side) can reuse it unchanged. */
export interface JobPostingSource extends CareerListItem {
  closesAt?: string | null;
  salary?: string | null;
}

/** schema.org JobPosting. Real fields only — validThrough/baseSalary appear only
 * when the row carries closes_at/salary (never fabricated, per 1.0.6.md §2.3). */
export function jobPostingJsonLd (vacancy: JobPostingSource): object {
  const json: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: vacancy.title,
    hiringOrganization: ORGANISATION_REF,
  };
  if (vacancy.postedAt) json.datePosted = vacancy.postedAt;
  if (vacancy.closesAt) json.validThrough = vacancy.closesAt;
  if (vacancy.summary) json.description = vacancy.summary;
  if (vacancy.employmentType && EMPLOYMENT_TYPE_MAP[vacancy.employmentType]) {
    json.employmentType = EMPLOYMENT_TYPE_MAP[vacancy.employmentType];
  }
  if (vacancy.location) {
    json.jobLocation = {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressLocality: vacancy.location, addressCountry: 'AU' },
    };
  }
  // salary is free text in content.careers — MonetaryAmount.value accepts Text,
  // so it passes through verbatim rather than being parsed into invented numbers.
  if (vacancy.salary) {
    json.baseSalary = { '@type': 'MonetaryAmount', currency: 'AUD', value: vacancy.salary };
  }
  return json;
}
